import { useEffect, useState } from 'react';

import Helper from 'Interfaces/Helper';
import CarouselItem from "MiniSiteComponents/Carousel/Carousel.interface";
import ComponentImage from "GeneralComponents/ComponentImage";
import TabContentTextBox from './TabsItem_TabContentTextBox';
import BottomTabLinks from './TabsItem_BottomTabLinks';
import useTabsA11y from './useTabsA11y';
import "./TabsCarousel.min.css";

type Props = {
    items: CarouselItem[];
    componentTitle: string;
    componentId: string;
    dir: string;
    updateSwiperAutoHeight?: () => void;
}

const TabsItem: React.FC<Props> = ({ componentId, items, componentTitle, dir, updateSwiperAutoHeight = () => { } }) => {

    const [activeIndex, setActiveIndex] = useState(0);
    const [focusedElementIdx, setFocusedElementIdx] = useState(-1);
    const [isTabpanelContentClamped, setIsTabpanelContentClamped] = useState(false);
    const [isContentExpended, setIsContentExpended] = useState(false);

    const contentBodyId = 'TabsCarousel-' + componentId + '-tabItem-content';
    const isTablet = window.matchMedia("(min-width: 768px)").matches;
    const A11yHelper = useTabsA11y({ componentId, dir });

    const CLASS = {
        tabs: "react-tabs",
        tablist: "react-tabs__tab-list",
        tab: "react-tabs__tab",
        tabSelected: "react-tabs__tab--selected",
        tabFocus: "tab--focus",
        tabpanelSelected: "react-tabs__tab-panel--selected"
    }

    // Functions
    const getTabCarousel = () => document.getElementById('Carousel-' + componentId.split("-")[0]);
    const getTabContainer = () => document.getElementById("carousel-tabs-" + componentId);
    const getTabContent = () => getTabContainer().querySelector("." + CLASS.tabpanelSelected);

    // Update swiper and a11y links
    const handleComponentUpdate = () => {
        const tabCarousel = getTabCarousel();
        if (tabCarousel != null) {
            const swiperEl = tabCarousel.querySelector("swiper-container");
            swiperEl.swiper?.update();
        }

        let selectedTabContent = getTabContent();
        let clampedContent = selectedTabContent.querySelector(".mst-tab-content-txt");

        if (isTablet) {
            const allLinks = clampedContent.querySelectorAll("a");
            Helper.GA4OuterClickEvent(allLinks);
        }
    }

    const checkLongContent = () => {
        let selectedTabContent = getTabContent();
        let clampedContent = selectedTabContent.querySelector(".mst-tab-content-txt-box");
        const isClamped = clampedContent && clampedContent.clientHeight < clampedContent.scrollHeight;

        setIsTabpanelContentClamped(isClamped)
    }

    const checkTabHeight = (i: number, isFocusEvent?: boolean) => {

        setActiveIndex(i);
        setIsContentExpended(false);
        setFocusedElementIdx(isFocusEvent ? i : - 1);
        //GA3
        try {
            let title = items[i].title;
            Helper.handleLinkClicksGA4('Tabs', componentTitle, title);
        }
        catch { }
    }

    const closeCollapseOnSlideChange = () => {
        const tabCarousel = getTabCarousel();
        if (tabCarousel != null) {
            const swiperEl = tabCarousel.querySelector("swiper-container");

            swiperEl?.swiper.on("activeIndexChange", () => {
                setIsContentExpended(false);
            })
        }
    }

    const showMore = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        try {
            let itemTitle = items[activeIndex].title;
            Helper.handleLinkClicksGA4("Tabs", componentTitle, itemTitle + "-more");
            setIsContentExpended(prev => !prev);
        }
        catch (e) { }
    }

    // Effects:

    // On mount
    useEffect(() => {
        checkLongContent();
        closeCollapseOnSlideChange();
    }, []);
    // On activeIdx change
    useEffect(() => {
        checkLongContent();
        handleComponentUpdate();
    }, [activeIndex]);
    // Update swiper on focused element change
    useEffect(() => { handleComponentUpdate(); }, [items, focusedElementIdx]);
    // Update swiper's auto-height on open/close show-more
    useEffect(() => { updateSwiperAutoHeight(); }, [isContentExpended]);


    return (
        <div className="mst-tabItem-content " id={"carousel-tabs-" + componentId} >

            <div className={CLASS.tabs} dir={dir}>

                <ul className={CLASS.tablist} role="tablist" >
                    {
                        items.map((tabitem, idx) => (
                            <li key={tabitem.index}
                                onClick={() => checkTabHeight(idx)}
                                onFocus={() => checkTabHeight(idx, true)}
                                onKeyDown={A11yHelper.onKeydown_tab}
                                tabIndex={activeIndex === idx ? 0 : -1}
                                role="tab"
                                aria-selected={activeIndex === idx}
                                aria-disabled="false"
                                aria-controls={`tabs-${componentId}-panel-${idx}`}
                                id={`tabs-${componentId}-tab-${idx}`}
                                className={`${CLASS.tab} ${activeIndex === idx ? CLASS.tabSelected : ''} ${focusedElementIdx === idx ? CLASS.tabFocus : ''}`}>
                                {tabitem.icon &&
                                    <i className={`${tabitem.icon}  minisite-font-36 pallete-color d-flex align-items-center justify-content-center mx-auto`}></i>
                                }

                                <div className="text-center">{tabitem.title}</div>

                            </li>
                        ))
                    }
                </ul>

                {items.map((tabitemcontent, idx) => (

                    <div key={tabitemcontent.index} className={`mst-tab-content ${activeIndex === idx ? CLASS.tabpanelSelected : 'd-none'}`}
                        role="tabpanel"
                        id={`tabs-${componentId}-panel-${idx}`}
                        aria-labelledby={`tabs-${componentId}-tab-${idx}`}>

                        <div className="mst-tab-content-txt ">

                            <div className={`mst-tab-content-txt-box collapse ${isTabpanelContentClamped && isContentExpended ? 'show' : ''}`}
                                id={contentBodyId} aria-expanded={isContentExpended}>

                                <TabContentTextBox tabitemcontent={tabitemcontent} isTablet={isTablet} />

                            </div>

                            <BottomTabLinks
                                tabitemcontent={tabitemcontent}
                                contentBodyId={contentBodyId}
                                componentId={componentId}
                                dir={dir}
                                isContentExpended={isContentExpended }
                                isTabpanelContentClamped={isTabpanelContentClamped}
                                onShowMoreClick={showMore} />

                            {
                                !isTablet &&
                                <ComponentImage
                                    image={tabitemcontent.image}
                                    imageClass=""
                                    mobileImage={tabitemcontent.mobileImage}
                                    mobileImageClass=""
                                    wrapperDivClass="pt-2 mst-tab-content-img" />
                            }

                        </div>

                    </div>
                ))}

            </div>
        </div>
    )

}

export default TabsItem;
